import { useParams, Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Newsletter from "@/components/feature/Newsletter";
import SEOSchema from "@/components/feature/SEOSchema";
import { articles, siteConfig, testimonials, categories } from "@/mocks/comics";

function getCategorySlug(categoryName: string): string {
  const cat = categories.find((c) => c.name === categoryName);
  return cat ? cat.slug : "romance";
}

const articleCoverUrls: Record<string, string> = {
  "article-01": "https://readdy.ai/api/search-image?query=Epic%20Korean%20manhwa%20style%20illustration%20in%20landscape%20format%20of%20a%20powerful%20shadow%20army%20rising%20behind%20a%20lone%20warrior%20silhouette%2C%20dramatic%20contrast%20between%20black%20shadows%20and%20purple%20energy%2C%20dark%20atmospheric%20backdrop%2C%20cinematic%20wide%20composition%2C%20webtoon%20aesthetic&width=1600&height=700&seq=article-01-hero&orientation=landscape",
  "article-02": "https://readdy.ai/api/search-image?query=Romantic%20Korean%20manhwa%20style%20illustration%20in%20landscape%20format%20of%20multiple%20couples%20in%20different%20settings%20like%20cherry%20blossom%20path%2C%20coffee%20shop%2C%20and%20starry%20night%2C%20soft%20pastel%20color%20palette%2C%20dreamy%20atmosphere%2C%20clean%20composition%2C%20webtoon%20aesthetic&width=1600&height=700&seq=article-02-hero&orientation=landscape",
  "article-03": "https://readdy.ai/api/search-image?query=Artistic%20Korean%20manhwa%20style%20illustration%20in%20landscape%20format%20showing%20the%20evolution%20of%20art%20styles%20from%20traditional%20ink%20drawing%20to%20modern%20digital%20webtoon%2C%20split%20composition%20with%20left%20side%20classic%20style%20and%20right%20side%20vibrant%20digital%20colors%2C%20clean%20aesthetic&width=1600&height=700&seq=article-03-hero&orientation=landscape",
};

export default function Article() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen pt-24 flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-foreground-950">文章未找到</h1>
            <Link to="/" className="mt-4 inline-block text-primary-500 hover:text-primary-600 cursor-pointer">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const coverUrl = articleCoverUrls[article.coverSeq] || articleCoverUrls["article-01"];
  const categorySlug = getCategorySlug(article.category);

  return (
    <>
      <SEOSchema
        type="WebPage"
        pageUrl={`/article/${slug}`}
        pageTitle={`${article.title} - ${siteConfig.name}`}
        pageDescription={article.excerpt}
        breadcrumbs={[
          { name: "首页", url: "/" },
          { name: article.category, url: `/category/${categorySlug}` },
          { name: article.title, url: `/article/${slug}` },
        ]}
        faqItems={article.faq}
        articleData={{
          title: article.title,
          description: article.excerpt,
          imageUrl: coverUrl,
          datePublished: article.date,
          dateModified: article.date,
          authorName: article.author,
        }}
      />

      <Navbar />

      <main>
        <article>
          <section className="relative w-full h-[350px] md:h-[480px] overflow-hidden">
            <img
              src={coverUrl}
              alt={article.title}
              className="absolute inset-0 w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-black/65" />
            <div className="relative z-10 h-full flex items-end w-full px-4 md:px-6 pb-8 md:pb-12">
              <div className="max-w-4xl">
                <nav className="flex items-center gap-2 text-xs md:text-sm text-background-50/70 mb-4">
                  <Link to="/" className="hover:text-background-50 transition-colors cursor-pointer">
                    首页
                  </Link>
                  <i className="ri-arrow-right-s-line text-[10px]" />
                  <Link
                    to={`/category/${categorySlug}`}
                    className="hover:text-background-50 transition-colors cursor-pointer"
                  >
                    {article.category}
                  </Link>
                  <i className="ri-arrow-right-s-line text-[10px]" />
                  <span className="text-background-50 truncate max-w-[200px]">{article.title}</span>
                </nav>
                <h1 className="text-2xl md:text-4xl lg:text-5xl font-black font-heading text-background-50 leading-tight">
                  {article.title}
                </h1>
                <div className="flex flex-wrap items-center gap-4 mt-4 text-xs md:text-sm text-background-50/70">
                  <span>{article.author}</span>
                  <span className="w-1 h-1 rounded-full bg-background-50/40" />
                  <span>{article.date}</span>
                  <span className="w-1 h-1 rounded-full bg-background-50/40" />
                  <span>{article.readTime}</span>
                  <span className="w-1 h-1 rounded-full bg-background-50/40" />
                  <span>{article.views} 阅读</span>
                </div>
              </div>
            </div>
          </section>

          <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-50">
            <div className="max-w-4xl mx-auto">
              <div className="flex flex-col lg:flex-row gap-8 md:gap-10">
                <article className="flex-1">
                  <div className="prose-custom text-sm md:text-base text-foreground-700 leading-relaxed space-y-5">
                    {article.content.split("\n").map((para, idx) => (
                      <p key={idx}>{para}</p>
                    ))}
                  </div>

                  <div className="mt-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 p-5 rounded-lg bg-background-100 border border-background-200">
                    <div>
                      <p className="text-xs text-foreground-400">作者</p>
                      <p className="text-sm font-semibold text-foreground-950 mt-0.5">{article.author}</p>
                    </div>
                    <div>
                      <p className="text-xs text-foreground-400">分类</p>
                      <p className="text-sm font-semibold text-foreground-950 mt-0.5">{article.category}</p>
                    </div>
                    <div>
                      <p className="text-xs text-foreground-400">发布日期</p>
                      <p className="text-sm font-semibold text-foreground-950 mt-0.5">{article.date}</p>
                    </div>
                    <button className="px-5 py-2.5 rounded-full text-xs font-semibold bg-primary-500 text-background-50 hover:bg-primary-600 transition-all cursor-pointer whitespace-nowrap">
                      <i className="ri-share-line mr-1.5" />
                      分享文章
                    </button>
                  </div>
                </article>

                <aside className="lg:w-72 flex-shrink-0">
                  <div className="sticky top-24">
                    <div className="rounded-lg overflow-hidden border border-background-200">
                      <img
                        src={coverUrl}
                        alt={article.title}
                        className="w-full aspect-[3/4] object-cover object-top"
                      />
                      <div className="p-4 bg-background-50">
                        <span className="inline-block px-2.5 py-1 rounded-full text-[10px] font-semibold bg-primary-100 text-primary-700 whitespace-nowrap">
                          热门文章
                        </span>
                        <h3 className="mt-2 text-sm font-bold font-heading text-foreground-950 line-clamp-2">
                          {article.title}
                        </h3>
                        <div className="flex items-center gap-4 mt-3 text-xs text-foreground-400">
                          <span>{article.views} 阅读</span>
                          <span>{article.readTime}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </aside>
              </div>
            </div>
          </section>

          {article.faq && article.faq.length > 0 && (
            <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-100">
              <div className="max-w-3xl mx-auto">
                <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950 text-center mb-10">
                  常见问题
                </h2>
                <div className="flex flex-col gap-4">
                  {article.faq.map((item, idx) => (
                    <details key={idx} className="group bg-background-50 rounded-lg border border-background-200 overflow-hidden">
                      <summary className="flex items-center justify-between p-5 cursor-pointer select-none">
                        <h3 className="text-sm md:text-base font-semibold text-foreground-950 pr-4">
                          {item.question}
                        </h3>
                        <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform flex-shrink-0" />
                      </summary>
                      <div className="px-5 pb-5">
                        <p className="text-sm text-foreground-600 leading-relaxed">
                          {item.answer}
                        </p>
                      </div>
                    </details>
                  ))}
                </div>
              </div>
            </section>
          )}

          <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-50">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950 text-center mb-10">
                读者热评
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {testimonials.slice(0, 3).map((item) => (
                  <div key={item.id} className="p-5 rounded-lg bg-background-100 border border-background-200">
                    <div className="flex items-center gap-0.5">
                      {Array.from({ length: 5 }).map((_, i) => (
                        <i
                          key={i}
                          className={`text-sm ${
                            i < item.rating
                              ? "ri-star-fill text-secondary-500"
                              : "ri-star-line text-foreground-300"
                          }`}
                        />
                      ))}
                    </div>
                    <p className="mt-3 text-xs md:text-sm text-foreground-500 leading-relaxed">
                      {item.content}
                    </p>
                    <div className="mt-4 flex items-center gap-3">
                      <img
                        src={item.avatar}
                        alt={item.name}
                        className="w-8 h-8 rounded-full object-cover"
                        loading="lazy"
                      />
                      <div>
                        <p className="text-xs font-semibold text-foreground-950">{item.name}</p>
                        <p className="text-[11px] text-foreground-400">{item.role}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        </article>

        <Newsletter />
      </main>

      <Footer />
    </>
  );
}